/**
 * axios 请求实例 (烘干厂 PC 端)
 *
 * 设计参考: liangyizhilian-pc-fe/src/utils/request.js
 * - Header 用 X-Access-Token (粮联 jeecg 风格)
 * - 响应结构: { success, code, message, result }
 * - 401 -> 尝试自动重登 (有本地凭证); 认证拒绝才清凭证跳登录,
 *   网络类失败保留登录态原地自愈 ([2026-09-27] 分级, 见 doSilentRelogin)
 * - 业务错误 (success=false) 不在拦截器里 message.error, 留给业务层处理特定错误码
 *
 * 注: 一期不引入 Vue.ls / store / VueAxios 等复杂封装, 用原生 localStorage 即可,
 *     避免循环依赖. 后续如需统一存储再迁移.
 */
import axios from 'axios'
import { message } from 'ant-design-vue'
import { loadCredentials, clearCredentials } from '@/utils/credentialStore'

// baseURL: 一期固定为 /api (对齐粮联 fe), 通过 vue.config.js devServer 代理到 gateway (9000)
// 实际请求路径: /api/sys/login → (pathRewrite) → http://127.0.0.1:9000/sys/login
// 生产环境通过 nginx 同源代理或 CORS 解决, 不依赖环境变量
const BASE_URL = '/api'

// ===== GET params 序列化: 数组展开为重复 key (key=v1&key=v2) =====
//
// axios 默认把数组参数写成 key[]=v1&key[]=v2 (带方括号), 而 Spring 的
// @RequestParam("key") List<String> 按精确参数名取值, 收不到 key[] → 数组过滤条件
// 静默失效 (实际案例: 财务待办 billTypes 丢参, 收款/付款两个待核查列表都变成全量).
// 这里全局统一改为 Spring 标准的重复 key 形式; 标量与编码行为对齐 axios 默认
// (跳过 null/undefined, 空格转 +, : $ , [ ] 不转义).
function serializeParams(params) {
  const encode = val =>
    encodeURIComponent(val)
      .replace(/%3A/gi, ':')
      .replace(/%24/g, '$')
      .replace(/%2C/gi, ',')
      .replace(/%20/g, '+')
      .replace(/%5B/gi, '[')
      .replace(/%5D/gi, ']')
  const parts = []
  Object.keys(params || {}).forEach(key => {
    const value = params[key]
    if (value === null || value === undefined) return
    if (Array.isArray(value)) {
      value.forEach(v => {
        if (v === null || v === undefined) return
        parts.push(encode(key) + '=' + encode(v))
      })
    } else if (typeof value === 'object') {
      // 兜底对齐 axios 默认行为: 嵌套对象写 key[sub] (项目内 GET 暂无此用法)
      Object.keys(value).forEach(sub => {
        const sv = value[sub]
        if (sv === null || sv === undefined) return
        parts.push(encode(key + '[' + sub + ']') + '=' + encode(sv))
      })
    } else {
      parts.push(encode(key) + '=' + encode(value))
    }
  })
  return parts.join('&')
}

const request = axios.create({
  baseURL: BASE_URL,
  // 30s: 部分接口冷启 (Nacos 校验/Druid 拿连接) 会超过 15s
  timeout: 30000,
  paramsSerializer: serializeParams
})

// ===== 请求拦截器: 注入 Token + factoryId 空值守卫 =====
request.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token')
    if (token) {
      // 粮联 jeecg 默认 Header
      config.headers['X-Access-Token'] = token
    }
    // [factoryId 空值守卫] 命中时先热补充 externalOrg 并回填放行; 补不回来才跳登录页
    // (见下方 guardEmptyFactoryId)
    return guardEmptyFactoryId(config) || config
  },
  err => Promise.reject(err)
)

// ===== [2026-09-08] factoryId 空值守卫: 热补充 externalOrg, 补不回来才引导重新登录 =====
//
// 根因: 业务页 factoryId 唯一给入时机是登录后 Login.vue 异步调 /drying/factory/me
//       (/sys/login 当前部署不返回 externalOrg). 该调用是 fire-and-forget (8s 超时, 失败仅
//       console.warn), 而 SET_LOGIN_INFO 在它返回前就已把登录快照落盘 (externalOrg=null).
//       一旦 factory/me 失败/超时/用户提前关页, 快照里就永久没有 externalOrg, 刷新后
//       hydrate 出来 factoryId="", 且运行时没有任何重新拉取的路径 → 所有业务接口带空
//       factoryId, 只有重新登录才再走一遍赋值链 ("重新登录就有了"的由来).
// 行为: 凡 /hl-drying/ 接口的请求参数里 factoryId 键存在但为空 → 先单飞拉 factory/me
//       热补充 (SET_EXTERNAL_ORG: store 响应式刷新各页 + 快照落盘自愈), 回填 factoryId
//       后放行原请求, 用户无感; 补不回来 (账号确实没绑厂 / 后端不可达) → toast 提示 +
//       清登录态跳登录页.
// 豁免: 平台超管 (DRY_SUPER_ADMIN) / 好粮超管 (SUPPER_ADMIN) 登录后需手动选厂,
//       选厂前 factoryId 本来就是空, 属正常状态, 不拦截.
const DRYING_API_RE = /\/hl-drying\//
let factoryIdGuardLastToastAt = 0

function snapshotRoleCodes() {
  try {
    const raw = localStorage.getItem('drying_auth_snapshot_v1')
    const snap = raw ? JSON.parse(raw) : null
    const codes = (snap && snap.loginUser && snap.loginUser.roleCodes) || []
    return Array.isArray(codes) ? codes : []
  } catch (e) {
    return [] // 快照损坏视为非超管, 守卫正常生效
  }
}

/** 判断请求是否"带 factoryId 键且值为空" (GET params / JSON body / FormData 三种载体) */
function carriesEmptyFactoryId(config) {
  const isEmpty = v => v === '' || v === null || v === undefined
  const params = config.params
  if (params && Object.prototype.hasOwnProperty.call(params, 'factoryId')) {
    return isEmpty(params.factoryId)
  }
  const data = config.data
  if (typeof data === 'string') {
    try {
      const body = JSON.parse(data)
      if (body && Object.prototype.hasOwnProperty.call(body, 'factoryId')) {
        return isEmpty(body.factoryId)
      }
    } catch (e) {
      // 非 JSON body (multipart 已在下面分支处理), 不视为带 factoryId
    }
  } else if (data && typeof data.has === 'function' && data.has('factoryId')) {
    return isEmpty(data.get('factoryId'))
  }
  return false
}

/**
 * [热补充] factoryId 丢失时拉 /drying/factory/me 补回 externalOrg (单飞, 并发请求共享同一次拉取).
 * 成功: commit SET_EXTERNAL_ORG — store 响应式刷新各页 factoryId computed + 登录快照落盘自愈
 * (此后刷新页面也不再丢), 返回 factoryId.
 * 401 (token 恰好过期): 先用本地凭证静默重登换新 token 再重试一次 — rawAxios 不经过响应拦截器,
 * 不补这一步的话, 用户空闲后回到页面首个带空 factoryId 的请求会绕过无感重登直接被踢登录页.
 * 失败 (后端不可达 / 账号确实没绑厂, factory/me 返回 null): reject → 上层走"提示重新登录"兜底.
 * 用 rawAxios (不带拦截器): 该接口不需要 factoryId, 不会递归触发本守卫; token 手动注入.
 */
function fetchMyFactory() {
  return rawAxios.get('/hl-drying/drying/factory/me', {
    timeout: 8000,
    headers: { 'X-Access-Token': localStorage.getItem('token') || '' }
  })
}

function fetchMyFactoryWithRelogin() {
  return fetchMyFactory().catch(err => {
    if (!(err && err.response && err.response.status === 401)) throw err
    return doSilentRelogin().then(() => fetchMyFactory())
  })
}

let orgRefillPromise = null
function refillExternalOrg() {
  if (orgRefillPromise) return orgRefillPromise
  orgRefillPromise = Promise.all([
    fetchMyFactoryWithRelogin(),
    import('@/store')
  ])
    .then(([resp, storeModule]) => {
      const data = resp && resp.data
      const my = data && data.success !== false ? data.result : null
      if (!my || !my.factoryId) throw new Error('factory/me 未返回厂信息')
      storeModule.default.commit('SET_EXTERNAL_ORG', my)
      return my.factoryId
    })
    .finally(() => {
      orgRefillPromise = null
    })
  return orgRefillPromise
}

/** 把补回来的 factoryId 写回原请求 (params / JSON body / FormData, 与检测逻辑对称) */
function applyFactoryId(config, factoryId) {
  const params = config.params
  if (params && Object.prototype.hasOwnProperty.call(params, 'factoryId')) {
    params.factoryId = factoryId
    return
  }
  const data = config.data
  if (typeof data === 'string') {
    try {
      const body = JSON.parse(data)
      if (body && Object.prototype.hasOwnProperty.call(body, 'factoryId')) {
        body.factoryId = factoryId
        config.data = JSON.stringify(body)
      }
    } catch (e) {
      // 非 JSON body, 无法回填 (与 carriesEmptyFactoryId 的检测分支一致, 正常不会走到)
    }
  } else if (data && typeof data.has === 'function' && data.has('factoryId')) {
    data.set('factoryId', factoryId)
  }
}

/**
 * 空 factoryId 守卫.
 * 命中"带 factoryId 且为空" → 先热补充 externalOrg, 成功则回填放行 (用户无感, 免重新登录);
 * 补不回来才 toast 提示重新登录 + 清登录态跳登录页. 未命中返回 null (请求放行).
 */
function guardEmptyFactoryId(config) {
  if (!DRYING_API_RE.test(config.url || '')) return null
  if (!carriesEmptyFactoryId(config)) return null
  const codes = snapshotRoleCodes()
  if (codes.includes('DRY_SUPER_ADMIN') || codes.includes('SUPPER_ADMIN')) return null
  return refillExternalOrg()
    .then(factoryId => {
      applyFactoryId(config, factoryId)
      return config
    })
    .catch(() => {
      const now = Date.now()
      if (now - factoryIdGuardLastToastAt > 3000) {
        factoryIdGuardLastToastAt = now
        message.error('登录信息已失效, 请重新登录', 5)
      }
      recordLogoutCause('factory-guard', 'factoryId 热补充失败 (factory/me 不可达或未绑厂)')
      // hardLogout: 清 token + 登录快照 + 桌面版凭据, hash 跳 #/login, 并 reject
      return hardLogout({ success: false, message: '登录信息已失效, 请重新登录' })
    })
}

// ===== 401 无感自动重登 (token 过期 / 失效时用本地凭证静默换新 token) =====
//
// 背景: 后端 token 有绝对过期时间 (空闲超 2h 失效). 即便有滑动续期, 用户长时间不操作仍会掉.
//       30s 通知轮询 (MainLayout) 会把"掉线"放大成"放一会就被踢回登录页 + 要重填验证码".
// 方案: 401 时若本地有"记住我"凭证, 用独立裸 axios 重新调 /sys/login 换新 token, 用户无感知.
//       重登成功 -> 用新 token 重放原请求 + 释放并发挂起队列;
//       重登失败 (密码改了/账号禁用/凭证损坏) -> 清凭证退化为手动登录.
//
// 关键: 重登必须用独立裸 axios (不经拦截器), 否则 login()→request→拦截器→login() 死循环.

// 独立裸实例, 仅用于自动重登 (不带拦截器, 不读 localStorage.token 注入头, 避免把过期 token 又带上)
const rawAxios = axios.create({ baseURL: BASE_URL, timeout: 60000 })

// 互斥: 在飞的重登 promise (null = 当前没有重登在进行).
// 并发规则: 第一个 401 创建重登 promise, 其余 401 走 pushToPendingQueue 挂起;
// 其他调用方 (如 guardEmptyFactoryId 的热补充) 并发调 doSilentRelogin 直接复用在飞 promise.
// (旧版用 isRefreshing + "永不 resolve" 的占位 promise, 误入会永久挂起)
let reloginPromise = null
let pendingQueue = []              // 重登期间其他 401 请求挂起, 重登成功后用新 token 重放

// 把当前请求挂起, 等重登拿到新 token 后再 resolve 重放
function pushToPendingQueue(config) {
  return new Promise((resolve, reject) => {
    pendingQueue.push({
      config,
      // resolve 时传入新 token, 由调用方据此重放请求
      onFulfilled: newToken => resolve(newToken),
      onRejected: err => reject(err)
    })
  })
}

// 重登成功: 通知队列里所有挂起请求用新 token 重放
function flushQueue(newToken) {
  pendingQueue.forEach(item => item.onFulfilled(newToken))
  pendingQueue = []
}

// 重登失败: 通知队列里所有挂起请求 reject, 并清空
function rejectQueue(err) {
  pendingQueue.forEach(item => item.onRejected(err))
  pendingQueue = []
}

/**
 * 用本地凭证静默重登, 返回新 token; 失败抛异常.
 * 互斥: 同一时间只有一个重登在进行, 其余调用方共享同一个 promise.
 *
 * [2026-09-27] 重登失败分级 (此前任何失败都清凭证+踢登录页, 一次网关抖动就被放大成客诉):
 * - 网络类失败 (无 response / 5xx): 退避重试 2 次 (1s / 3s); 仍失败由调用方原地保留登录态.
 * - 认证拒绝 (后端 2xx 明确返回 success=false, 如密码被改/账号禁用/组织失效):
 *   标记 authRejected 不重试, 由调用方清凭证退化为手动登录 — 旧凭证留着只会反复失败.
 * @returns {Promise<string>} 新 token
 */
function isTransientReloginError(err) {
  if (err && err.authRejected) return false
  if (err && err.response) {
    return err.response.status >= 500
  }
  return true // 无 response = 网络错误 / 超时
}

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

function reloginOnce(cred) {
  return rawAxios
    .post('/jeecg-system/sys/login', {
      username: cred.username,
      password: cred.password,
      // automaticLogin='true' -> 后端给 14 天 TTL, 减少再次掉线
      automaticLogin: 'true'
    })
    .then(resp => {
      const data = resp && resp.data
      if (data && data.success && data.result && data.result.token) {
        const newToken = data.result.token
        localStorage.setItem('token', newToken)
        return newToken
      }
      const e = new Error((data && data.message) || 'relogin bad response')
      e.authRejected = true
      throw e
    })
}

function doSilentRelogin() {
  if (reloginPromise) return reloginPromise
  const cred = loadCredentials()
  if (!cred) {
    return Promise.reject(new Error('no credentials'))
  }
  const attempt = n =>
    reloginOnce(cred).catch(err => {
      if (isTransientReloginError(err) && n < 2) {
        return sleep(n === 0 ? 1000 : 3000).then(() => attempt(n + 1))
      }
      throw err
    })
  reloginPromise = attempt(0).finally(() => {
    reloginPromise = null
  })
  return reloginPromise
}

// [2026-09-27] 登出/重登失败原因落盘 (只留最近一条), 事后排查"为什么被踢"直接看 localStorage
// drying_auth_incident_v1 = { ts, kind, detail }
//   kind: relogin-auth-rejected | relogin-network | no-credentials | login-api-401 | factory-guard
const AUTH_INCIDENT_KEY = 'drying_auth_incident_v1'
function recordLogoutCause(kind, detail) {
  try {
    localStorage.setItem(AUTH_INCIDENT_KEY, JSON.stringify({
      ts: new Date().toISOString(),
      kind: kind,
      detail: detail ? String(detail).slice(0, 200) : ''
    }))
  } catch (e) {
    // localStorage 异常不影响主流程
  }
  // eslint-disable-next-line no-console
  console.warn('[request] 登录态事件:', kind, detail || '')
}

// 硬登出: 无凭证 / 认证拒绝时, 清状态跳登录页
function hardLogout(rejectObj) {
  localStorage.removeItem('token')
  // 同时清持久化的登录态 snapshot, 避免下次启动 hydrate 出过期数据
  try { localStorage.removeItem('drying_auth_snapshot_v1') } catch (e) {}
  // [2026-08-12] 桌面版：连独立凭据文件里的 token/snapshot 一起清，否则下次启动会被恢复（自动登回）
  try { window.desktop && window.desktop.credentials && window.desktop.credentials.clearToken() } catch (e) {}
  // 用 hash 跳转避免循环 import router
  if (typeof window !== 'undefined' && window.location.hash !== '#/login') {
    window.location.hash = '#/login'
  }
  return Promise.reject(rejectObj)
}

// 网络类重登失败的提示节流 (10s 一条, 避免轮询接口刷屏)
let networkFailToastAt = 0

// ===== 响应拦截器 =====
request.interceptors.response.use(
  res => {
    const data = res.data
    // 粮联 jeecg Result 结构: { success, code, message, result }
    if (data && data.success === false) {
      // 业务错误, 不在这里 message.error (交给业务层按错误码 [LOGIN_011]/[PWD_001] 等做处理)
      return Promise.reject(data)
    }
    // 兼容: 有的接口直接返 data, 有的返 { result: ... }
    return data && data.result !== undefined ? data.result : data
  },
  err => {
    const response = err && err.response
    // 401: token 过期 / 无效, 尝试自动重登, 失败再硬登出
    if (response && response.status === 401) {
      const originalConfig = err.config
      // 登录页本身的请求 401 (凭证错) 不自动重登, 直接返回让登录页处理
      const isLoginApi = originalConfig && /\/sys\/login(\?|$)/.test(originalConfig.url || '')

      // 正在重登: 当前请求挂起, 等队列重放
      if (reloginPromise && !isLoginApi) {
        return pushToPendingQueue(originalConfig).then(newToken => {
          originalConfig.headers['X-Access-Token'] = newToken
          return request(originalConfig)
        })
      }

      // 无本地凭证 或 登录接口 401: 直接硬登出
      const cred = loadCredentials()
      if (!cred || isLoginApi) {
        recordLogoutCause(isLoginApi ? 'login-api-401' : 'no-credentials', originalConfig && originalConfig.url)
        return hardLogout(response.data || { success: false, message: '登录已过期, 请重新登录' })
      }

      // 首个 401: 触发自动重登
      return doSilentRelogin()
        .then(newToken => {
          flushQueue(newToken)
          // 用新 token 重放当前请求
          originalConfig.headers['X-Access-Token'] = newToken
          return request(originalConfig)
        })
        .catch(refreshErr => {
          rejectQueue(refreshErr)
          // [2026-09-27] 分级处理:
          // - 认证拒绝 (密码改了/账号禁用/组织失效): 凭证已废, 清掉并踢回登录页手动登录;
          // - 网络类失败 (网关抖动/后端不可达): 不清 token、不跳登录页, 用户留在当前页,
          //   网络恢复后下一个 401 请求会再次自动重登, 无感自愈.
          //   (旧逻辑两者都 clearCredentials + hardLogout, 一次网络抖动就造成"突然被踢+要重输验证码")
          const authRejected = !!(refreshErr && refreshErr.authRejected)
          if (authRejected) {
            recordLogoutCause('relogin-auth-rejected', refreshErr && refreshErr.message)
            clearCredentials()
            return hardLogout({ success: false, message: '登录已过期, 请重新登录' })
          }
          recordLogoutCause('relogin-network', refreshErr && refreshErr.message)
          const now = Date.now()
          if (now - networkFailToastAt > 10000) {
            networkFailToastAt = now
            message.error('网络不稳定，恢复后将自动重试登录', 5)
          }
          return Promise.reject({ success: false, message: '网络不稳定, 请稍后重试', network: true })
        })
    }
    // 其他网络/服务异常
    if (response && response.data) {
      return Promise.reject(response.data)
    }
    return Promise.reject({ success: false, message: err && err.message ? err.message : '网络异常, 请稍后重试' })
  }
)

export default request
